import { Button, Card, CardMedia, Dialog, DialogContent, DialogProps, DialogTitle, Divider, Link, Menu, MenuItem, useTheme } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser';
import { calcPatientDetailRoute } from 'app/main/patients/calcPatientDetailRoute';
import { Form, Formik, FormikHelpers } from 'formik';
import { ColorOnHoverIconButton } from 'framework/components/buttons/ColorOnHoverIconButton';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { ExpandLessOrMoreIcon } from 'framework/components/ExpandLessOrMoreIcon';
import { LabelledPropertyX } from 'framework/components/labelledProperty/LabelledPropertyX';
import { DateFilterItem } from 'framework/filter/date/DateFilterItem';
import { FilterBar } from 'framework/filter/FilterBar';
import { RecordListFilterComponent } from 'framework/filter/RecordListFilterComponent';
import { CancelSubmitFormDialogActions } from 'framework/forms/CancelSubmitFormDialogActions';
import { FormDatePicker } from 'framework/forms/FormDatePicker';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { useAnchorElement } from 'framework/hooks/useAnchorElement';
import { useApiCall } from 'framework/hooks/useApiCall';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { useQueryContext } from 'framework/hooks/useQueryContext';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import { createEmptyHeader } from 'framework/table/createEmptyHeader';
import { createHeader } from 'framework/table/createHeader';
import { PageableTable } from 'framework/table/PageableTable';
import { TableWithHeadersAndValues } from 'framework/table/TableWithHeadersAndValues';
import { onResetToPage1 } from 'framework/types/onResetToPage1';
import { clearParams } from 'framework/utils/clearParams';
import { formatDate } from 'framework/utils/date/formatDate';
import { isFiltered } from 'framework/utils/isFiltered';
import {
	audiogramStandardsQuery_tonalAudiogramImage,
	CochlearPatientExportProp,
	CochlearPatientExportProps,
	CochlearPatientStatus,
	CochlearPatientStatuses,
	exportCochlearPatients_handle,
	getCochlearReferralCenters_handle,
	ICochlearAudiogramStandard,
	ICochlearPatientsFilterParams,
	ICochlearPatientsFilterSortPageParams,
	ICochlearPatientSummaryDto,
	ICochlearReferralCenterDto,
	ICochlearSettingsDto,
	ICochlearToneThresholdAudiogram,
	IPatchCochlearPatientBimodallyFittedDateRequest,
	IPatchCochlearPatientImplantedDateRequest,
	IPatchCochlearPatientInformedDateRequest,
	IPatchCochlearPatientReferralCenterRequest,
	IPatchCochlearPatientReferralDateRequest,
	ITonalAudiogramImage,
	patchCochlearPatientBimodallyFittedDate_handle,
	patchCochlearPatientImplantedDate_handle,
	patchCochlearPatientInformedDate_handle,
	patchCochlearPatientReferralCenter_handle,
	patchCochlearPatientReferralDate_handle,
} from 'gen/ApiClient';
import Fg1 from 'libs/ui/layout/fg1';
import ColumnStack from 'libs/ui/layout/stack/column.stack';
import RowStack from 'libs/ui/layout/stack/row.stack';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AudiogramImageOrEmpty } from 'shared/audiograms/AudiogramImageOrEmpty';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { ExportButtonMenuAndDialog } from 'shared/export/ExportButtonMenuAndDialog';
import { IReloadTriggerProps } from 'shared/layout/IReloadTriggerProps';
import { v1 as uuid } from 'uuid';
import * as yup from 'yup';
import {
	CiCandidateChip,
	CochlearPatientsQueryContext,
	CochlearPatientStatusChip,
	createCochlearPatientStatusRecord,
	RegisterAsCochlearSpecialistButton,
	RegisterPatientForCochlearEventDialog,
} from './cochlear.components';
import { FormSelectFieldOnId } from 'framework/forms/FormSelectFieldOnId';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { Searchbar } from 'framework/components/search/Searchbar';
import { FilterOffOutlineIcon } from 'framework/components/icons/FilterOffOutlineIcon';

function createExportRecord(strings: IStrings): Record<CochlearPatientExportProp, string> {
	return {
		AddressLine: strings.addressLine,
		BimodallyFittedDate: strings.bimodallyFittedDate,
		City: strings.city,
		Country: strings.country,
		DateOfBirth: strings.dateOfBirth,
		FirstName: strings.firstName,
		FormattedAddress: strings.address,
		Gender: strings.gender,
		Id: strings.id,
		ImplantedDate: strings.implantedDate,
		InformedDate: strings.informedDate,
		IsCiCandidate: `${strings.ciCandidate}?`,
		LastName: strings.lastName,
		MaxPta: 'Max. PTA',
		ReferralDate: strings.referralDate,
		SocialSecurityNumber: strings.socialSecurityNumber,
		Zip: strings.zip,
		Email: strings.email,
	};
}

type CochlearPatientsTabProps = IReloadTriggerProps & {
	reload: VoidFunction;
	settings: ICochlearSettingsDto;
};

export const CochlearPatientsTab = ({ reloadTrigger, settings }: CochlearPatientsTabProps) => {
	const strings = useLocalization();
	const theme = useTheme();
	const { params, setParams, queryResult, reload } = useQueryContext(CochlearPatientsQueryContext);
	const { push } = useHistory();
	const { open, cancel } = useDialogsContext();

	useLazyEffect(() => {
		reload();
	}, [reloadTrigger]);

	const viewAudiogramsDialog = (item: ICochlearPatientSummaryDto) => {
		open(
			<Dialog
				open
				onClose={cancel}>
				<AudiogramsStack item={item} />
			</Dialog>
		);
	};

	const headers = useMemo(
		() => [
			createHeader<ICochlearPatientSummaryDto>(strings.patient, t => (
				<RowStack
					dfrowac
					gap={1}>
					<TextLinkButton
						underline
						onClick={() => push(calcPatientDetailRoute(t.id))}>
						{t.data.identifier}
					</TextLinkButton>
					{t.data.isCiCandidate && <CiCandidateChip patientId={t.id} />}
				</RowStack>
			)),
			createHeader<ICochlearPatientSummaryDto>(strings.socialSecurityNumber, t => t.socialSecurityNumber),
			createHeader<ICochlearPatientSummaryDto>(strings.dateOfBirth, t => formatDate(t.dateOfBirth, 'only-date')),
			createHeader<ICochlearPatientSummaryDto>(strings.address, t => t.formattedAddress),
			createHeader<ICochlearPatientSummaryDto>(strings.email, t => <Link href={`mailto:${t.email}`}>{t.email}</Link>),
			createHeader<ICochlearPatientSummaryDto>(strings.status, t => <CochlearPatientStatusChip status={t.data.status} />),
			createHeader<ICochlearPatientSummaryDto>(`Max PTA`, t => (t.data.maxPta ? `${t.data.maxPta} dB HL` : '')),
			createEmptyHeader<ICochlearPatientSummaryDto>('chevron', (item, _, isExpanded, toggleExpand) => (
				<RowStack dfrowac>
					<Fg1 />
					<ColorOnHoverIconButton
						edge='end'
						size='small'
						color='primary'
						disabled={item.data.audiograms.length === 0}
						onClick={() => viewAudiogramsDialog(item)}>
						<OpenInBrowserIcon />
					</ColorOnHoverIconButton>
					<ColorOnHoverIconButton
						edge='end'
						size='small'
						color='primary'
						onClick={toggleExpand}>
						<ExpandLessOrMoreIcon isExpanded={isExpanded === true} />
					</ColorOnHoverIconButton>
				</RowStack>
			)),
		],
		// eslint-disable-next-line
		[strings]
	);

	if (queryResult === undefined) {
		return <div></div>;
	}

	return (
		<RowStack
			dfrow
			w100
			style={{ padding: '16px', gap: '8px' }}>
			<Card className='w100'>
				<div
					className='df-row-ac jc-sb'
					style={{ padding: theme.spacing(1) }}>
					<div className='df-row-ac'>
						<Button
							onClick={() => setParams(onResetToPage1({ ...params, isCiCandidate: true }))}
							color={params.isCiCandidate === true ? 'primary' : 'default'}
							variant='text'
							style={params.isCiCandidate === true ? { backgroundColor: 'rgba(25, 118, 210, 0.1)' } : {}}>
							{strings.onlyCiCandidates}
						</Button>
						<Button
							onClick={() => setParams(onResetToPage1({ ...params, isCiCandidate: undefined }))}
							color={params.isCiCandidate === undefined ? 'primary' : 'default'}
							variant='text'
							style={params.isCiCandidate === undefined ? { backgroundColor: 'rgba(25, 118, 210, 0.1)' } : {}}>
							{strings.allPatients}
						</Button>
					</div>
					<div>
						<RegisterAsCochlearSpecialistButton settings={settings} />
						<ExportButtonMenuAndDialog<ICochlearPatientsFilterParams, CochlearPatientExportProp>
							params={params}
							filteredCount={queryResult.filteredCount}
							unfilteredCount={queryResult.unfilteredCount}
							downloadF={exportCochlearPatients_handle}
							fileName={strings.patients}
							record={createExportRecord(strings)}
							allProps={CochlearPatientExportProps}
							view='cochlear_patients'
						/>
					</div>
				</div>
				<Divider />
				<div className='df-row jc-sb'>
					<Searchbar
						variant='embedded'
						value={params.searchString}
						placeholder={strings.searchVerb}
						onSearch={text => setParams({ ...params, searchString: text })}
					/>
				</div>
				<Divider />
				<div className='df-row jc-sb'>
					<Filter
						params={params}
						onFilter={params => setParams(onResetToPage1(params))}
					/>
				</div>
				<Divider />
				<CardMedia>
					<PageableTable<ICochlearPatientSummaryDto>
						queryResult={queryResult}
						headers={headers}
						sortParams={params.sortParams}
						pageParams={params.pageParams}
						setParams={(sp, pp) => setParams({ ...params, sortParams: sp, pageParams: pp })}
						getKey={inst => inst.id}
						expandableContent={item => (
							<DetailStack
								item={item}
								reload={reload}
							/>
						)}
					/>
				</CardMedia>
			</Card>
		</RowStack>
	);
};

const exclusions: (keyof ICochlearPatientsFilterSortPageParams)[] = ['isCiCandidate'];

type FilterProps = {
	params: ICochlearPatientsFilterSortPageParams;
	onFilter: (params: ICochlearPatientsFilterSortPageParams) => void;
};

const Filter = ({ params, onFilter }: FilterProps) => {
	const strings = useLocalization();
	const record = createCochlearPatientStatusRecord(strings);

	return (
		<FilterBar
			isFiltered={isFiltered(params, exclusions)}
			onClear={() => onFilter(clearParams(params, exclusions))}>
			<RecordListFilterComponent<CochlearPatientStatus>
				options={CochlearPatientStatuses}
				record={record}
				selected={params.statuses as any}
				label={strings.status}
				icon={<HelpOutlineIcon />}
				setSelected={vals => onFilter({ ...params, statuses: vals })}
			/>
			<DateFilterItem
				label={strings.informedDate}
				after={params.informedDateAfter}
				before={params.informedDateBefore}
				setSelected={(after, before) => onFilter({ ...params, informedDateAfter: after, informedDateBefore: before })}
			/>
			<DateFilterItem
				label={strings.referralDate}
				after={params.referralDateAfter}
				before={params.referralDateBefore}
				setSelected={(after, before) => onFilter({ ...params, referralDateAfter: after, referralDateBefore: before })}
			/>
			<DateFilterItem
				label={strings.implantedDate}
				after={params.implantedDateAfter}
				before={params.implantedDateBefore}
				setSelected={(after, before) => onFilter({ ...params, implantedDateAfter: after, implantedDateBefore: before })}
			/>
			<DateFilterItem
				label={strings.bimodallyFittedDate}
				after={params.bimodallyFittedDateAfter}
				before={params.bimodallyFittedDateBefore}
				setSelected={(after, before) => onFilter({ ...params, bimodallyFittedDateAfter: after, bimodallyFittedDateBefore: before })}
			/>
			{params.patientId && (
				<TextLinkButton
					color='error'
					startIcon={<FilterOffOutlineIcon />}
					onClick={() => onFilter({ ...params, patientId: undefined })}>
					{strings.patient}
				</TextLinkButton>
			)}
		</FilterBar>
	);
};

type DetailStackProps = {
	item: ICochlearPatientSummaryDto;
	reload: VoidFunction;
};

const DetailStack = ({ item, reload }: DetailStackProps) => {
	const { open, confirm, cancel } = useDialogsContext(reload);

	const onRegisterForEvent = () => {
		open(
			<RegisterPatientForCochlearEventDialog
				open
				confirm={confirm}
				cancel={cancel}
				patientId={item.id}
				eventId={undefined}
			/>
		);
	};

	const onPatchBimodallyFittedDate = () => {
		open(
			<PatchCochlearPatientBimodallyFittedDateForm
				open
				confirm={confirm}
				cancel={cancel}
				item={item}
			/>
		);
	};

	const onPatchImplantedDate = () => {
		open(
			<PatchCochlearPatientImplantedDateForm
				open
				confirm={confirm}
				cancel={cancel}
				item={item}
			/>
		);
	};

	const onPatchInformedDate = () => {
		open(
			<PatchCochlearPatientInformedDateForm
				open
				confirm={confirm}
				cancel={cancel}
				item={item}
			/>
		);
	};

	const onPatchReferralDate = () => {
		open(
			<PatchCochlearPatientReferralDateForm
				open
				confirm={confirm}
				cancel={cancel}
				item={item}
			/>
		);
	};

	const onPatchReferralCenter = () => {
		open(
			<PatchCochlearPatientReferralCenterForm
				open
				confirm={confirm}
				cancel={cancel}
				item={item}
			/>
		);
	};

	return (
		<ColumnStack
			dfcol
			style={{ marginBottom: '16px', gap: '8px' }}>
			<LabelledPropertyX
				label='Datum geïnformeerd'
				edit={onPatchInformedDate}>
				<div>{formatDate(item.data.informedDate)}</div>
			</LabelledPropertyX>
			<LabelledPropertyX
				label='Datum doorverwezen'
				edit={onPatchReferralDate}>
				<div>{formatDate(item.data.referralDate)}</div>
			</LabelledPropertyX>
			<LabelledPropertyX
				label='Doorverwezen naar centrum'
				edit={onPatchReferralCenter}>
				<div>{item.referralCenterName}</div>
			</LabelledPropertyX>
			<LabelledPropertyX
				label='Datum geïmplanteerd'
				edit={onPatchImplantedDate}>
				<div>{formatDate(item.data.implantedDate)}</div>
			</LabelledPropertyX>
			<LabelledPropertyX
				label='Datum bimodaal aangepast'
				edit={onPatchBimodallyFittedDate}>
				<div>{formatDate(item.data.bimodallyFittedDate)}</div>
			</LabelledPropertyX>
			<div style={{ fontSize: 20 }}>{`Deelgenomen aan`}</div>
			{item.data.events.length === 0 && (
				<div>
					<em>{`Geen events`}</em>
				</div>
			)}
			{item.data.events.map(t => (
				<RowStack
					dfrowac
					gap={1}
					key={t.eventId}>
					<div>
						<strong>{formatDate(t.date)}</strong>
					</div>
					<div>{t.title}</div>
				</RowStack>
			))}
			<div>
				<Button
					color='primary'
					variant='contained'
					startIcon={<AddIcon />}
					onClick={onRegisterForEvent}
					size='small'>
					{`Schrijf patiënt in voor event`}
				</Button>
			</div>
		</ColumnStack>
	);
};

type AudiogramsStackProps = {
	item: ICochlearPatientSummaryDto;
};

const AudiogramsStack = ({ item }: AudiogramsStackProps) => {
	const strings = useLocalization();
	const theme = useTheme();
	// const [selectedId, setSelectedId] = useState<string>();
	const audiograms = useMemo(() => _.orderBy(item.data.audiograms, t => t.noahActionCreatedDate, 'desc'), [item.data.audiograms]);
	const [audiogram, setAudiogram] = useState<ICochlearAudiogramStandard>();
	const ttas = useMemo(() => audiogram?.toneThresholdAudiograms, [audiogram]);
	const [image, setImage] = useState<ITonalAudiogramImage>();
	const imageData = image?.bothEarImage;

	const loadImage = useApiCall(audiogramStandardsQuery_tonalAudiogramImage);

	const [anchor, open, close] = useAnchorElement();

	const onSelectAudiogram = async (id: string) => {
		setAudiogram(undefined);
		const fAudiogram = audiograms.find(t => t.id === id);
		if (fAudiogram) {
			setAudiogram(fAudiogram);
			const r = await loadImage(fAudiogram.id);
			setImage(r.isSuccess ? r.result : undefined);
		}
	};

	useEffect(() => {
		const setAndLoad = async (fAudiogram: ICochlearAudiogramStandard) => {
			setAudiogram(fAudiogram);
			const r = await loadImage(fAudiogram.id);
			setImage(r.isSuccess ? r.result : undefined);
		};

		if (audiograms.length > 0) {
			setAndLoad(audiograms[0]);
		}
		// eslint-disable-next-line
	}, [audiograms]);

	const headers = useMemo(
		() => [
			createHeader<ICochlearToneThresholdAudiogram>(`Output`, item => item.signalOutputTypeDisplay),
			createHeader<ICochlearToneThresholdAudiogram>(`Condition`, item => item.hearingInstrumentConditionTypeDisplay),
			createHeader<ICochlearToneThresholdAudiogram>(`PTA berekening`, item => item.ptaRecord.leftSideEquation),
			createHeader<ICochlearToneThresholdAudiogram>(`PTA resultaat`, item => item.ptaRecord.rightSideEquation),
		],
		// eslint-disable-next-line
		[strings]
	);

	return (
		<ColumnStack style={{ padding: '8px' }}>
			<Menu
				anchorEl={anchor}
				open={Boolean(anchor)}
				onClose={close}>
				{audiograms.map(t => (
					<MenuItem
						key={t.id}
						value={t.id}
						onClick={() => {
							onSelectAudiogram(t.id);
							close();
						}}>
						{formatDate(t.noahActionCreatedDate, 'date-and-time')}
					</MenuItem>
				))}
			</Menu>
			<div>
				<Button
					onClick={open}
					color='primary'
					variant='outlined'
					size='small'
					endIcon={Boolean(anchor) ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
					style={{
						fontSize: '16px',
						fontWeight: 500,
						letterSpacing: '0.15008px',
						lineHeight: '24px',
					}}>
					{formatDate(audiogram?.noahActionCreatedDate, 'date-and-time')}
				</Button>
			</div>
			<RowStack dfrowac_jcc>
				<AudiogramImageOrEmpty
					imageData={imageData}
					style={{ maxWidth: 450, width: '100%' }}
				/>
			</RowStack>
			{audiogram !== undefined && ttas !== undefined && ttas.length > 0 && (
				<TableWithHeadersAndValues<ICochlearToneThresholdAudiogram>
					padding={theme.spacing(1)}
					headers={headers}
					getKey={() => uuid()}
					values={ttas}
				/>
			)}
		</ColumnStack>
	);
};

const createIPatchCochlearPatientBimodallyFittedDateRequestSchema = (_: IStrings) => {
	return yup.object<IPatchCochlearPatientBimodallyFittedDateRequest>({
		date: yup.date(),
		id: yup.string().required(),
	});
};

type PatchCochlearPatientBimodallyFittedDateFormProps = DialogProps & {
	item: ICochlearPatientSummaryDto;
	confirm: VoidFunction;
	cancel: VoidFunction;
};

const PatchCochlearPatientBimodallyFittedDateForm = ({ item, confirm, cancel, ...rest }: PatchCochlearPatientBimodallyFittedDateFormProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [update, isSubmitting] = useFormSubmit(patchCochlearPatientBimodallyFittedDate_handle);

	const handleSubmit = async (
		values: IPatchCochlearPatientBimodallyFittedDateRequest,
		helpers: FormikHelpers<IPatchCochlearPatientBimodallyFittedDateRequest>
	) => {
		const r = await update(values);
		if (handleFormResponse(r, helpers)) {
			notify(strings.patientUpdated);
			confirm();
		}
	};

	return (
		<Formik<IPatchCochlearPatientBimodallyFittedDateRequest>
			validateOnMount
			validationSchema={createIPatchCochlearPatientBimodallyFittedDateRequestSchema(strings)}
			initialValues={{
				date: item.data.bimodallyFittedDate,
				id: item.id,
			}}
			onSubmit={handleSubmit}>
			<Form>
				<Dialog
					fullWidth
					{...rest}>
					<DialogTitle>{`Wijzig datum bimodaal aangepast`}</DialogTitle>
					<DialogContent
						dividers
						className='df-col'
						style={{ overflow: 'visible' }}>
						<FormDatePicker<IPatchCochlearPatientBimodallyFittedDateRequest>
							name='date'
							label={`Datum bimodaal aangepast`}
							required={false}
						/>
					</DialogContent>
					<CancelSubmitFormDialogActions
						submitText={strings.update}
						isSubmitting={isSubmitting}
						cancel={cancel}
					/>
				</Dialog>
			</Form>
		</Formik>
	);
};

const createIPatchCochlearPatientImplantedDateRequestSchema = (_: IStrings) => {
	return yup.object<IPatchCochlearPatientImplantedDateRequest>({
		date: yup.date(),
		id: yup.string().required(),
	});
};

type PatchCochlearPatientImplantedDateFormProps = DialogProps & {
	item: ICochlearPatientSummaryDto;
	confirm: VoidFunction;
	cancel: VoidFunction;
};

const PatchCochlearPatientImplantedDateForm = ({ item, confirm, cancel, ...rest }: PatchCochlearPatientImplantedDateFormProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [update, isSubmitting] = useFormSubmit(patchCochlearPatientImplantedDate_handle);

	const handleSubmit = async (values: IPatchCochlearPatientImplantedDateRequest, helpers: FormikHelpers<IPatchCochlearPatientImplantedDateRequest>) => {
		const r = await update(values);
		if (handleFormResponse(r, helpers)) {
			notify(strings.patientUpdated);
			confirm();
		}
	};

	return (
		<Formik<IPatchCochlearPatientImplantedDateRequest>
			validateOnMount
			validationSchema={createIPatchCochlearPatientImplantedDateRequestSchema(strings)}
			initialValues={{
				date: item.data.implantedDate,
				id: item.id,
			}}
			onSubmit={handleSubmit}>
			<Form>
				<Dialog
					fullWidth
					{...rest}>
					<DialogTitle>{`Wijzig datum geïmplanteerd`}</DialogTitle>
					<DialogContent
						dividers
						className='df-col'
						style={{ overflow: 'visible' }}>
						<FormDatePicker<IPatchCochlearPatientImplantedDateRequest>
							name='date'
							label={`Datum geïmplanteerd`}
							required={false}
						/>
					</DialogContent>
					<CancelSubmitFormDialogActions
						submitText={strings.update}
						isSubmitting={isSubmitting}
						cancel={cancel}
					/>
				</Dialog>
			</Form>
		</Formik>
	);
};

const createIPatchCochlearPatientInformedDateRequestSchema = (_: IStrings) => {
	return yup.object<IPatchCochlearPatientInformedDateRequest>({
		date: yup.date(),
		id: yup.string().required(),
	});
};

type PatchCochlearPatientInformedDateFormProps = DialogProps & {
	item: ICochlearPatientSummaryDto;
	confirm: VoidFunction;
	cancel: VoidFunction;
};

const PatchCochlearPatientInformedDateForm = ({ item, confirm, cancel, ...rest }: PatchCochlearPatientInformedDateFormProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [update, isSubmitting] = useFormSubmit(patchCochlearPatientInformedDate_handle);

	const handleSubmit = async (values: IPatchCochlearPatientInformedDateRequest, helpers: FormikHelpers<IPatchCochlearPatientInformedDateRequest>) => {
		const r = await update(values);
		if (handleFormResponse(r, helpers)) {
			notify(strings.patientUpdated);
			confirm();
		}
	};

	return (
		<Formik<IPatchCochlearPatientInformedDateRequest>
			validateOnMount
			validationSchema={createIPatchCochlearPatientInformedDateRequestSchema(strings)}
			initialValues={{
				date: item.data.informedDate,
				id: item.id,
			}}
			onSubmit={handleSubmit}>
			<Form>
				<Dialog
					fullWidth
					{...rest}>
					<DialogTitle>{`Wijzig datum geïnformeerd`}</DialogTitle>
					<DialogContent
						dividers
						className='df-col'
						style={{ overflow: 'visible' }}>
						<FormDatePicker<IPatchCochlearPatientInformedDateRequest>
							name='date'
							label={`Datum geïnformeerd`}
							required={false}
						/>
					</DialogContent>
					<CancelSubmitFormDialogActions
						submitText={strings.update}
						isSubmitting={isSubmitting}
						cancel={cancel}
					/>
				</Dialog>
			</Form>
		</Formik>
	);
};

const createIPatchCochlearPatientReferralDateRequestSchema = (_: IStrings) => {
	return yup.object<IPatchCochlearPatientReferralDateRequest>({
		date: yup.date(),
		id: yup.string().required(),
	});
};

type PatchCochlearPatientReferralDateFormProps = DialogProps & {
	item: ICochlearPatientSummaryDto;
	confirm: VoidFunction;
	cancel: VoidFunction;
};

const PatchCochlearPatientReferralDateForm = ({ item, confirm, cancel, ...rest }: PatchCochlearPatientReferralDateFormProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [update, isSubmitting] = useFormSubmit(patchCochlearPatientReferralDate_handle);

	const handleSubmit = async (values: IPatchCochlearPatientReferralDateRequest, helpers: FormikHelpers<IPatchCochlearPatientReferralDateRequest>) => {
		const r = await update(values);
		if (handleFormResponse(r, helpers)) {
			notify(strings.patientUpdated);
			confirm();
		}
	};

	return (
		<Formik<IPatchCochlearPatientReferralDateRequest>
			validateOnMount
			validationSchema={createIPatchCochlearPatientReferralDateRequestSchema(strings)}
			initialValues={{
				date: item.data.referralDate,
				id: item.id,
			}}
			onSubmit={handleSubmit}>
			<Form>
				<Dialog
					fullWidth
					{...rest}>
					<DialogTitle>{`Wijzig datum doorverwezen`}</DialogTitle>
					<DialogContent
						dividers
						className='df-col'
						style={{ overflow: 'visible' }}>
						<FormDatePicker<IPatchCochlearPatientReferralDateRequest>
							name='date'
							label={`Datum doorverwezen`}
							required={false}
						/>
					</DialogContent>
					<CancelSubmitFormDialogActions
						submitText={strings.update}
						isSubmitting={isSubmitting}
						cancel={cancel}
					/>
				</Dialog>
			</Form>
		</Formik>
	);
};

const createIPatchCochlearPatientReferralCenterRequestSchema = (_: IStrings) => {
	return yup.object<IPatchCochlearPatientReferralCenterRequest>({
		id: yup.string().required(),
		referralCenterId: yup.string(),
	});
};

type PatchCochlearPatientReferralCenterFormProps = DialogProps & {
	item: ICochlearPatientSummaryDto;
	confirm: VoidFunction;
	cancel: VoidFunction;
};

const PatchCochlearPatientReferralCenterForm = ({ item, confirm, cancel, ...rest }: PatchCochlearPatientReferralCenterFormProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [update, isSubmitting] = useFormSubmit(patchCochlearPatientReferralCenter_handle);
	const [referralCenters] = useApiEffect(getCochlearReferralCenters_handle);

	const handleSubmit = async (values: IPatchCochlearPatientReferralCenterRequest, helpers: FormikHelpers<IPatchCochlearPatientReferralCenterRequest>) => {
		const r = await update(values);
		if (handleFormResponse(r, helpers)) {
			notify(strings.patientUpdated);
			confirm();
		}
	};

	return (
		<Formik<IPatchCochlearPatientReferralCenterRequest>
			validateOnMount
			validationSchema={createIPatchCochlearPatientReferralCenterRequestSchema(strings)}
			initialValues={{
				id: item.id,
				referralCenterId: item.data.referralCenterId,
			}}
			onSubmit={handleSubmit}>
			<Form>
				<Dialog
					fullWidth
					{...rest}>
					<DialogTitle>{`Wijzig datum doorverwezen`}</DialogTitle>
					<DialogContent
						dividers
						className='df-col'
						style={{ overflow: 'visible' }}>
						<FormSelectFieldOnId<IPatchCochlearPatientReferralCenterRequest, ICochlearReferralCenterDto>
							name='referralCenterId'
							label={`Centrum`}
							options={referralCenters ?? []}
							renderValue={t => t.name}
							getKey={t => t.id}
						/>
					</DialogContent>
					<CancelSubmitFormDialogActions
						submitText={strings.update}
						isSubmitting={isSubmitting}
						cancel={cancel}
					/>
				</Dialog>
			</Form>
		</Formik>
	);
};
